import React, { useState, useEffect, useMemo } from 'react';
import {
    Box,
    Typography,
    IconButton,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    CircularProgress,
    Alert
} from '@mui/material';
import {
    ArrowBack as ArrowBackIcon,
    NavigateBefore as NavigateBeforeIcon,
    NavigateNext as NavigateNextIcon
} from '@mui/icons-material';
import { format } from 'date-fns';
import { enUS } from 'date-fns/locale';
import { getWeekTimecards } from '../../../services/timecardService';
import { useAuth } from '../../../context/AuthContext';
import ExportButton from '../../../components/common/ExportButton';
import { getWeekInfo } from '../../../utils/dateUtils';

// Utility functions
const getLocalDateString = (date) => {
    const localDate = new Date(date.getTime() - date.getTimezoneOffset() * 60000);
    return localDate.toISOString().split('T')[0];
};

const getLocalSunday = (date) => {
    const localDate = new Date(date.getTime() - date.getTimezoneOffset() * 60000);
    const day = localDate.getDay();
    const diff = localDate.getDate() - day;
    return new Date(localDate.setDate(diff));
};

const utcToLocal = (utcDateString) => {
    const date = new Date(utcDateString);
    return new Date(date.getTime() + date.getTimezoneOffset() * 60000);
};

const getWeekNumber = (date) => {
    return format(date, 'w');
};

const WeeklyProjectsViewAll = ({ onBack }) => {
    const [dataLoading, setDataLoading] = useState(false);
    const [error, setError] = useState(null);
    const [timecards, setTimecards] = useState([]);
    const [currentDate, setCurrentDate] = useState(new Date());
    const { user, loading: authLoading } = useAuth();

    // Memoize currentSunday to prevent unnecessary recalculations
    const currentSunday = useMemo(() => getLocalSunday(currentDate), [currentDate]);
    
    // Memoize week number
    const weekNumber = useMemo(() => getWeekNumber(currentSunday), [currentSunday]);

    // Fetch timecards when date changes
    useEffect(() => {
        let mounted = true;
        const controller = new AbortController();

        const fetchTimecardData = async () => {
            try {
                setDataLoading(true);
                const dateString = getLocalDateString(currentSunday);
                
                console.log('Fetching timecards for all projects:', {
                    dateString,
                    weekNumber,
                    userRole: user?.role
                });
                
                // Explicitly request all users' data
                const data = await getWeekTimecards(dateString, null, 'all');
                if (mounted) {
                    console.log('Timecards received:', {
                        count: data.length,
                        weekNumber
                    });
                    
                    // Filter timecards based on user role and week
                    let filteredTimecards = [...data];
                    
                    // Additional filtering to ensure timecards are only shown in the correct week
                    filteredTimecards = filteredTimecards.filter(timecard => {
                        // Calculate the week number for this timecard's start date
                        const timecardStartDate = new Date(timecard.startDate);
                        const timecardWeekInfo = getWeekInfo(timecardStartDate);
                        
                        // Instead of comparing week numbers (which can be inconsistent),
                        // check if the timecard's date range overlaps with the current week's date range
                        
                        // Get the current week's date range
                        const currentWeekStart = new Date(currentSunday);
                        const currentWeekEnd = new Date(currentSunday);
                        currentWeekEnd.setDate(currentWeekEnd.getDate() + 6);
                        
                        // Get the timecard's start and end dates
                        const tcStartDate = new Date(timecard.startDate);
                        const tcEndDate = new Date(timecard.endDate);
                        
                        // A timecard belongs to this week if its date range overlaps with the current week's date range
                        // This is true if:
                        // 1. The timecard's start date is within the current week, OR
                        // 2. The timecard's end date is within the current week, OR
                        // 3. The timecard spans the entire current week
                        const isCorrectWeek = 
                            // Case 1: Timecard starts within the week
                            (tcStartDate >= currentWeekStart && tcStartDate <= currentWeekEnd) ||
                            // Case 2: Timecard ends within the week
                            (tcEndDate >= currentWeekStart && tcEndDate <= currentWeekEnd) ||
                            // Case 3: Timecard spans the entire week
                            (tcStartDate <= currentWeekStart && tcEndDate >= currentWeekEnd);
                        
                        console.log('Frontend timecard week check (WeeklyProjectsViewAll):', {
                            id: timecard._id,
                            user: timecard.user?.username || 'Unknown',
                            project: timecard.project?.name || 'Unknown',
                            timecardWeekNumber: timecardWeekInfo.weekNumber,
                            timecardYear: timecardWeekInfo.year,
                            currentWeekNumber: weekNumber,
                            isCorrectWeek
                        });
                        
                        return isCorrectWeek;
                    });
                    
                    console.log('Week timecards (after week filtering):', {
                        beforeCount: data.length,
                        afterCount: filteredTimecards.length,
                        weekNumber
                    });
                    
                    // If user is a team lead, filter to only show projects from their teams
                    if (user.role === 'team_lead') {
                        console.log('User is a team lead, filtering timecards by project');
                        
                        // Get project IDs for the teams where the user is the team lead
                        let teamProjectIds = [];
                        
                        // Check if user has teams
                        if (user.teams && user.teams.length > 0) {
                            // Get only the teams where the user is the team lead
                            const leadTeams = user.teams.filter(team => 
                                String(team.teamLead) === String(user.id)
                            );
                            
                            if (leadTeams.length > 0) {
                                // Get all projects from these teams
                                const teamProjects = leadTeams.flatMap(team => team.projects || []);
                                
                                // Make sure we don't have duplicates
                                teamProjectIds = [...new Set(teamProjects.map(id => String(id)))];
                                
                                console.log('Team project IDs:', teamProjectIds);
                            }
                        }
                        
                        // If we have ledTeams, try to get projects from there
                        if (teamProjectIds.length === 0 && user.ledTeams && user.ledTeams.length > 0) {
                            console.log('Using ledTeams:', user.ledTeams);
                            
                            // Find the corresponding full teams for these ledTeams
                            const ledTeamIds = user.ledTeams.map(team => team._id);
                            
                            // Find the full team objects from the user.teams array
                            const fullLedTeams = user.teams ? user.teams.filter(team => 
                                ledTeamIds.some(id => String(id) === String(team._id))
                            ) : [];
                            
                            // Get all projects from these teams
                            const teamProjects = fullLedTeams.flatMap(team => team.projects || []);
                            
                            // Make sure we don't have duplicates
                            teamProjectIds = [...new Set(teamProjects.map(id => String(id)))];
                            
                            console.log('Team project IDs from ledTeams:', teamProjectIds);
                        }
                        
                        // Filter timecards to only show those for team projects
                        if (teamProjectIds.length > 0) {
                            filteredTimecards = filteredTimecards.filter(timecard => 
                                timecard.project && teamProjectIds.includes(String(timecard.project._id))
                            );
                            
                            console.log('Filtered timecards count:', filteredTimecards.length);
                        } else {
                            console.log('No team projects found, showing all timecards');
                        }
                    }
                    
                    setTimecards(filteredTimecards);
                    setError(null);
                }
            } catch (err) {
                if (err.name === 'AbortError') {
                    console.log('Fetch aborted');
                    return;
                }
                console.error('Error fetching timecards:', err);
                if (mounted) {
                    setError(err.message);
                    setTimecards([]);
                }
            } finally {
                if (mounted) {
                    setDataLoading(false);
                }
            }
        };

        // Only fetch if we have a user
        if (user) {
            fetchTimecardData();
        }

        return () => {
            mounted = false;
            controller.abort();
        };
    }, [currentSunday, weekNumber, user]);

    const handlePreviousWeek = () => {
        setCurrentDate(prev => {
            const newDate = new Date(prev);
            newDate.setDate(newDate.getDate() - 7);
            return newDate;
        });
    };

    const handleNextWeek = () => {
        setCurrentDate(prev => {
            const newDate = new Date(prev);
            newDate.setDate(newDate.getDate() + 7);
            return newDate;
        });
    };

    // Memoize week info calculation
    const weekInfo = useMemo(() => {
        const days = Array.from({ length: 7 }, (_, i) => {
            const date = new Date(currentSunday);
            date.setDate(currentSunday.getDate() + i);
            return {
                date: date.toISOString()
            };
        });
        
        return { days };
    }, [currentSunday]);

    const { days } = weekInfo;

    // Show loading state while auth is loading
    if (authLoading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                <CircularProgress />
            </Box>
        );
    }

    // Show error if no user data
    if (!user) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                <Alert severity="error">Authentication required</Alert>
            </Box>
        );
    }

    // Get date range for filename
    const dateRange = timecards.length > 0 
        ? `${format(utcToLocal(timecards[0].days[0].date), 'MMM_d', { locale: enUS })}_to_${format(utcToLocal(timecards[0].days[timecards[0].days.length - 1].date), 'MMM_d', { locale: enUS })}`
        : `${format(currentSunday, 'MMM_d')}_to_${format(new Date(days[days.length - 1].date), 'MMM_d')}`;

    // Group and aggregate timecards by project
    const projectSummaries = timecards.reduce((projects, timecard) => {
        const projectId = timecard.project._id;
        if (!projects[projectId]) {
            projects[projectId] = {
                project: timecard.project,
                days: timecard.days.map(() => ({ hours: 0 })),
                total: 0
            };
        }

        // Add hours
        timecard.days.forEach((day, index) => {
            projects[projectId].days[index].hours += parseFloat(day.hours);
        });

        // Update project total
        projects[projectId].total += timecard.days.reduce((sum, day) => sum + parseFloat(day.hours), 0);

        return projects;
    }, {});

    return (
        <Box sx={{ 
            p: 3,
            '@media (max-width:600px)': {
                p: 1, // Much more reduced padding for mobile
                paddingTop: 0, // No top padding - layout provides spacing
                paddingBottom: '88px', // Account for bottom navigation
            },
            '@media (max-width:900px) and (orientation: landscape)': {
                p: 0.5, // Minimal padding in landscape mode
                paddingTop: 0, // No top padding
                paddingBottom: 0.5, // Minimal bottom padding
                height: 'auto', // Allow natural height
                overflow: 'visible', // Allow content to overflow
                minHeight: 'auto', // Remove height constraints
            }
        }}>
            {error && (
                <Alert severity="error" sx={{ mb: 3 }}>
                    {error}
                </Alert>
            )}

            {/* Header */}
            <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                mb: 3,
                '@media (max-width:600px)': {
                    mb: 1, // Much more reduced margin for mobile
                },
                '@media (max-width:900px) and (orientation: landscape)': {
                    mb: 0.5, // Reduced margin in landscape mode but keep header visible
                }
            }}>
                <IconButton onClick={onBack} sx={{ mr: 2 }}>
                    <ArrowBackIcon />
                </IconButton>
                <Typography variant="h6">
                    Weekly View - All Projects
                </Typography>
            </Box>

            {/* Navigation Bar */}
            <Paper sx={{ 
                p: 2, 
                mb: 3,
                '@media (max-width:600px)': {
                    p: 0, // Remove all padding on mobile
                    mb: 1.5,
                }
            }}>
                <Box sx={{ 
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    alignItems: 'center',
                    '@media (max-width:600px)': {
                        flexDirection: 'row', // Keep on same line for mobile
                        gap: 0.5,
                        alignItems: 'center',
                        px: 0.5, // Add minimal padding inside the flex container
                        py: 1,
                    }
                }}>
                    <Box sx={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        gap: 1,
                        '@media (max-width:600px)': {
                            flex: 1,
                            gap: 0.5,
                            marginLeft: '-8px', // Move the entire week navigation widget to the left
                        }
                    }}>
                        <IconButton 
                            onClick={handlePreviousWeek}
                            sx={{
                                '@media (max-width:600px)': {
                                    padding: '4px',
                                }
                            }}
                        >
                            <NavigateBeforeIcon />
                        </IconButton>
                        <Typography sx={{
                            '@media (max-width:600px)': {
                                fontSize: '0.75rem',
                                textAlign: 'center',
                                flex: 1,
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                            }
                        }}>
                            Week {weekNumber}: {format(currentSunday, 'MMM d', { locale: enUS })} - {format(new Date(days[days.length - 1].date), 'MMM d, yyyy', { locale: enUS })}
                        </Typography>
                        <IconButton 
                            onClick={handleNextWeek}
                            sx={{
                                '@media (max-width:600px)': {
                                    padding: '4px',
                                }
                            }}
                        >
                            <NavigateNextIcon />
                        </IconButton>
                    </Box>
                    <ExportButton
                        viewType="weekly"
                        timecards={timecards}
                        viewMode="allProjects"
                        isAllUsers={true}
                        user={{ username: 'all_projects' }}
                        date={dateRange}
                        mobileIconOnly={true}
                    />
                </Box>
            </Paper>

            {/* Project Summary Table */}
            <TableContainer component={Paper}>
                <Table size="small">
                    <TableHead sx={{ bgcolor: '#e0e0e0' }}>
                        <TableRow>
                            <TableCell sx={{ 
                                width: '15%',
                                '@media (max-width:900px)': {
                                    width: '25%',
                                    fontSize: '0.75rem',
                                    padding: '8px',
                                }
                            }}>Project</TableCell>
                            <TableCell sx={{ 
                                width: '9%', 
                                whiteSpace: 'nowrap',
                                '@media (max-width:900px)': {
                                    width: '25%',
                                    fontSize: '0.75rem',
                                    padding: '8px',
                                    minWidth: '100px',
                                }
                            }}>Date Range</TableCell>
                            {days.map((day, index) => {
                                const date = new Date(day.date);
                                return (
                                    <TableCell 
                                        key={index} 
                                        align="center" 
                                        sx={{ 
                                            width: '10%',
                                            '@media (max-width:900px)': {
                                                fontSize: '0.7rem',
                                                padding: '4px',
                                                minWidth: '50px',
                                            }
                                        }}
                                    >
                                        <Box sx={{
                                            display: 'block',
                                            '@media (max-width:900px)': {
                                                display: 'none',
                                            }
                                        }}>
                                            {format(date, 'EEE', { locale: enUS })}<br />
                                            {format(date, 'MMM d', { locale: enUS })}
                                        </Box>
                                        <Box sx={{
                                            display: 'none',
                                            '@media (max-width:900px)': {
                                                display: 'block',
                                            }
                                        }}>
                                            {format(date, 'M/d', { locale: enUS })}
                                        </Box>
                                    </TableCell>
                                );
                            })}
                            <TableCell align="center" sx={{
                                '@media (max-width:900px)': {
                                    fontSize: '0.75rem',
                                    padding: '8px',
                                }
                            }}>Total</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {Object.keys(projectSummaries).length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={10} align="center">
                                    No timecard data found for this week
                                </TableCell>
                            </TableRow>
                        ) : Object.values(projectSummaries).map(({ project, days: projectDays, total }) => (
                            <React.Fragment key={project._id}>
                                <TableRow>
                                    <TableCell sx={{
                                        '@media (max-width:900px)': {
                                            fontSize: '0.75rem',
                                            padding: '8px',
                                        }
                                    }}>{project.name}</TableCell>
                                    <TableCell sx={{
                                        whiteSpace: 'nowrap',
                                        '@media (max-width:900px)': {
                                            fontSize: '0.75rem',
                                            whiteSpace: 'nowrap',
                                            padding: '8px',
                                        }
                                    }}>
                                        <Box sx={{
                                            display: 'block',
                                            '@media (max-width:900px)': {
                                                display: 'none',
                                            }
                                        }}>
                                            {format(currentSunday, 'MMM d', { locale: enUS })} - {format(new Date(days[days.length - 1].date), 'MMM d', { locale: enUS })}
                                        </Box>
                                        <Box sx={{
                                            display: 'none',
                                            '@media (max-width:900px)': {
                                                display: 'block',
                                            }
                                        }}>
                                            {format(currentSunday, 'M/d', { locale: enUS })} - {format(new Date(days[days.length - 1].date), 'M/d', { locale: enUS })}
                                        </Box>
                                    </TableCell>
                                    {projectDays.map((day, index) => (
                                        <TableCell key={index} align="center" sx={{
                                            '@media (max-width:900px)': {
                                                fontSize: '0.75rem',
                                                padding: '4px',
                                            }
                                        }}>
                                            {day.hours.toFixed(2)}
                                        </TableCell>
                                    ))}
                                    <TableCell align="center" sx={{
                                        '@media (max-width:900px)': {
                                            fontSize: '0.75rem',
                                            padding: '8px',
                                        }
                                    }}>
                                        {total.toFixed(2)}
                                    </TableCell>
                                </TableRow>
                            </React.Fragment>
                        ))}
                        {/* Total row - only show if there are project summaries */}
                        {Object.keys(projectSummaries).length > 0 && (
                            <TableRow sx={{ bgcolor: '#f8f8d7' }}>
                                <TableCell>Total</TableCell>
                                <TableCell />
                                {days.map((_, index) => {
                                    const dayTotal = Object.values(projectSummaries).reduce((sum, { days }) => {
                                        return sum + days[index].hours;
                                    }, 0);
                                    return (
                                        <TableCell key={index} align="center">
                                            {dayTotal.toFixed(2)}
                                        </TableCell>
                                    );
                                })}
                                <TableCell align="center">
                                    {Object.values(projectSummaries).reduce((sum, { total }) => sum + total, 0).toFixed(2)}
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </TableContainer>
            
            {dataLoading && (
                <Box sx={{ 
                    position: 'absolute', 
                    top: 0, 
                    left: 0, 
                    right: 0, 
                    bottom: 0, 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'center',
                    backgroundColor: 'rgba(255, 255, 255, 0.7)',
                    zIndex: 1000
                }}>
                    <CircularProgress />
                </Box>
            )}
        </Box>
    );
};

export default WeeklyProjectsViewAll;
